import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ArrowLeftRight,
  BarChart3,
  BellRing,
  Bike,
  Boxes,
  ClipboardList,
  FileSpreadsheet,
  Flame,
  FolderTree,
  LogOut,
  Mail,
  MapPin,
  MessageSquare,
  Package,
  PackageCheck,
  Percent,
  QrCode,
  RotateCcw,
  ScrollText,
  Settings,
  Store,
  TrendingUp,
  Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EsLogo } from "@/components/Shell";
import { NotificationBell } from "@/components/NotificationBell";

const LINKS = [
  { to: "/admin", label: "Tableau de bord", icon: BarChart3, roles: ["admin"], end: true },
  { to: "/admin/produits", label: "Produits", icon: Package, roles: ["admin"] },
  { to: "/admin/offres", label: "Offres du jour", icon: Flame, roles: ["admin"] },
  { to: "/admin/categories", label: "Catégories", icon: FolderTree, roles: ["admin"] },
  { to: "/admin/commandes", label: "Commandes", icon: ClipboardList, roles: ["admin", "warehouse"] },
  { to: "/admin/stock", label: "Stock / Entrepôt", icon: Boxes, roles: ["admin", "warehouse"] },
  { to: "/admin/preparation", label: "Préparation", icon: PackageCheck, roles: ["admin", "warehouse"] },
  { to: "/admin/retraits", label: "Retraits", icon: QrCode, roles: ["admin", "warehouse"] },
  { to: "/admin/retours", label: "Retours", icon: RotateCcw, roles: ["admin", "warehouse"] },
  { to: "/admin/transferts", label: "Transferts stock", icon: ArrowLeftRight, roles: ["admin", "warehouse"] },
  { to: "/admin/livreurs", label: "Livreurs", icon: Bike, roles: ["admin", "warehouse"] },
  { to: "/admin/exports", label: "Exports Excel", icon: FileSpreadsheet, roles: ["admin", "warehouse"] },
  { to: "/admin/rapport-mensuel", label: "Rapport mensuel", icon: Mail, roles: ["admin"] },
  { to: "/admin/relances", label: "Relances paiement", icon: BellRing, roles: ["admin"] },
  { to: "/admin/sms", label: "Alertes SMS", icon: MessageSquare, roles: ["admin"] },
  { to: "/admin/analyses", label: "Analyses", icon: TrendingUp, roles: ["admin"] },
  { to: "/admin/vendeurs", label: "Vendeurs", icon: Store, roles: ["admin"] },
  { to: "/admin/commissions", label: "Commissions", icon: Percent, roles: ["admin"] },
  { to: "/admin/clients", label: "Clients", icon: Users, roles: ["admin"] },
  { to: "/admin/zones", label: "Zones de livraison", icon: MapPin, roles: ["admin"] },
  { to: "/admin/parametres", label: "Paramètres", icon: Settings, roles: ["admin"] },
  { to: "/admin/journal", label: "Journal", icon: ScrollText, roles: ["admin"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = LINKS.filter((l) => l.roles.includes(user?.role));
  const [pending, setPending] = useState({ vendors: 0, products: 0 });

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/vendors/pending-count").then((r) => setPending(r.data)).catch(() => {});
  }, [user?.role]);

  const badgeFor = (to) => (to === "/admin/vendeurs" ? pending.vendors + pending.products : 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 backdrop-blur">
        <div className="mx-auto flex max-w-[1500px] items-center gap-3 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <EsLogo className="h-9 w-9" />
            <div className="hidden sm:block">
              <p className="font-display text-sm font-extrabold leading-none tracking-tight text-white">
                EMPIRE<span className="text-gold">-SHOP</span>
              </p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                {user?.role === "admin" ? "Espace administrateur" : "Espace entrepôt"}
              </p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell dark />
            <button type="button" onClick={() => navigate("/")} className="rounded-xl border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800" data-testid="admin-goto-shop">
              <Store className="mr-1 inline h-3.5 w-3.5" /> Boutique
            </button>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/connexion");
              }}
              className="rounded-xl border border-rose-900 px-3 py-2 text-xs font-semibold text-rose-300 hover:bg-rose-950"
              data-testid="admin-logout"
            >
              <LogOut className="mr-1 inline h-3.5 w-3.5" /> Quitter
            </button>
          </div>
        </div>
        <nav className="no-scrollbar mx-auto flex max-w-[1500px] gap-1 overflow-x-auto px-4 pb-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                  isActive ? "bg-brand text-white" : "text-slate-300 hover:bg-slate-800"
                }`
              }
              data-testid={`admin-nav-${l.to.split("/").pop() || "dashboard"}`}
            >
              <l.icon className="h-4 w-4" /> {l.label}
              {badgeFor(l.to) > 0 && (
                <span className="font-num grid h-4 min-w-4 place-items-center rounded-full bg-gold px-1 text-[10px] font-bold text-white" data-testid="admin-nav-vendors-badge">
                  {badgeFor(l.to)}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-[1500px] px-4 py-5">
        <Outlet />
      </main>
    </div>
  );
}

export const Panel = ({ title, subtitle, actions, children, testid }) => (
  <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4" data-testid={testid}>
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="font-display text-lg font-bold text-white">{title}</h2>
        {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
    {children}
  </section>
);

export const AInput = (props) => (
  <input
    {...props}
    className={`w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-brand ${props.className || ""}`}
  />
);

export const ASelect = ({ children, ...props }) => (
  <select
    {...props}
    className={`w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-brand ${props.className || ""}`}
  >
    {children}
  </select>
);

export const ALabel = ({ children }) => (
  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{children}</label>
);

export const ABtn = ({ children, variant = "primary", ...props }) => {
  const styles = {
    primary: "bg-brand text-white hover:bg-brand-dark",
    gold: "bg-gold text-white hover:bg-gold-dark",
    ghost: "border border-slate-700 text-slate-200 hover:bg-slate-800",
    danger: "border border-rose-900 text-rose-300 hover:bg-rose-950",
  };
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${styles[variant]} ${props.className || ""}`}
    >
      {children}
    </button>
  );
};
